/**
 * Safe storage wrapper.
 *
 * Why this exists:
 *   iOS Safari in Private Browsing mode exposes sessionStorage/localStorage
 *   but every setItem call throws QuotaExceededError (max-0 bytes).
 *   Without this wrapper, sessionStorage.setItem("token", ...) in our auth
 *   flow crashes the login handler and the user sees a blank screen.
 *
 * Strategy:
 *   1. Try sessionStorage (preferred — session-only)
 *   2. Fall back to localStorage (some iOS versions allow it)
 *   3. Fall back to in-memory Map (current tab only, but login still works)
 */

const memoryStore = new Map();

function tryStorage(getStore, key, value) {
  try {
    const store = getStore();
    if (value === undefined) return store.getItem(key);
    if (value === null) { store.removeItem(key); return null; }
    store.setItem(key, value);
    return value;
  } catch (_e) {
    return undefined;
  }
}

export const safeStorage = {
  getItem(key) {
    let v = tryStorage(() => window.sessionStorage, key);
    if (v !== undefined && v !== null) return v;
    v = tryStorage(() => window.localStorage, key);
    if (v !== undefined && v !== null) return v;
    return memoryStore.get(key) || null;
  },
  setItem(key, value) {
    const vSession = tryStorage(() => window.sessionStorage, key, value);
    if (vSession !== undefined) return;
    const vLocal = tryStorage(() => window.localStorage, key, value);
    if (vLocal !== undefined) return;
    memoryStore.set(key, value);
  },
  removeItem(key) {
    tryStorage(() => window.sessionStorage, key, null);
    tryStorage(() => window.localStorage, key, null);
    memoryStore.delete(key);
  },
};
